import { preserveAnonymiser } from "@thalamos/common";

import { defaultAnonymiser } from "../../anonymisation/defaultAnonymimser.js";
import {
  combineIncidentAnonymisers,
  type FormAnonymiser,
  type IncidentWorkItemAnonymiser,
  type SectionAnonymiser,
} from "../../anonymisation/IncidentWorkItemAnonymiser.js";
import { locationFuzzerAnonymiser } from "../../anonymisation/util.js";
import { FormSectionSchema } from "../../types/FormSection.js";
import { IncidentFormTypeSchema } from "../../types/incidentWorkItem.js";
import type { ArrivePlaceOfSafetyFormData } from "./arrivePlaceOfSafety.js";
import type { CreateIncidentWorkItemFormData } from "./createIncident.js";
import type { DetentionDetailsFormData } from "./detentionDetails.js";
import type { FinishFormData } from "./finishForm.js";
import type { InformationForHealthcareFormData } from "./informationForHealthcare.js";
import type { LeavePlaceOfSafetyFormData } from "./leavePlaceOfSafety.js";
import type { McaCapacityAssessmentFormData } from "./mcaCapacityAssessment.js";
import type { McaIncidentDetailsFormData } from "./mcaIncidentDetails.js";
import type { McaPersonDetailsFormData } from "./mcaPersonDetails.js";
import type { PersonDetailsFormData } from "./personDetails.js";
import type { PreEndIncidentFormData } from "./preEndIncident.js";
import type { RecordPlaceOfSafetyFormData } from "./recordPlaceOfSafety.js";
import type { RequestPolicePresenceMainFormData } from "./requestPolicePresenceMain.js";
import type { TriageIncidentFormData } from "./triageIncident.js";

const createIncidentMainAnonymiser: SectionAnonymiser<CreateIncidentWorkItemFormData> =
  {
    sectionId: FormSectionSchema.enum.createIncidentMain,
    fieldAnonymisers: {
      dispatchNumberCreated: preserveAnonymiser,
    },
  };

const createIncidentAnonymiser: FormAnonymiser = {
  formType: IncidentFormTypeSchema.enum.createIncident,
  sectionAnonymisers: [createIncidentMainAnonymiser],
};

const triageIncidentMainAnonymiser: SectionAnonymiser<TriageIncidentFormData> =
  {
    sectionId: FormSectionSchema.enum.triageMain,
    fieldAnonymisers: {
      reasonNotConsulted: preserveAnonymiser,
      givenHealthCareProfessionalAdvice: preserveAnonymiser,
      voluntaryAction: preserveAnonymiser,
      howDidYouContactNhs: preserveAnonymiser,
    },
  };

export const triageIncidentAnonymiser: FormAnonymiser = {
  formType: IncidentFormTypeSchema.enum.triage,
  sectionAnonymisers: [triageIncidentMainAnonymiser],
};

const personDetailstMainAnonymiser: SectionAnonymiser<PersonDetailsFormData> = {
  sectionId: FormSectionSchema.enum.personDetails,
  fieldAnonymisers: {
    gender: preserveAnonymiser,
    ethnicAppearance: preserveAnonymiser,
    selfDeclaredEthnicity: preserveAnonymiser,
    relativeOrFriendSameAddress: preserveAnonymiser,
    relativeOrFriendInformed: preserveAnonymiser,
  },
};

const detentionDetailsAnonymiser: SectionAnonymiser<DetentionDetailsFormData> =
  {
    sectionId: FormSectionSchema.enum.detentionDetails,
    fieldAnonymisers: {
      orgResponsibleForDetention: preserveAnonymiser,
      locationDetentionTookPlace: locationFuzzerAnonymiser,
      londonBoroughDetentionTookPlace: preserveAnonymiser,
      locationType: preserveAnonymiser,
      confirmCustodySuite: preserveAnonymiser,
      userIsAttendingOfficer: preserveAnonymiser,
      createdOnBehalfOfAnotherOfficerWithShoulderNumber: preserveAnonymiser,
      confirmPersonAddress: preserveAnonymiser,
      incidentLocation: locationFuzzerAnonymiser,
      commandUnit: preserveAnonymiser,
      londonBoroughIncidentTookPlace: preserveAnonymiser,
      incidentAndLocationSameLocation: preserveAnonymiser,
      locationTypeIncident: preserveAnonymiser,
    },
  };

const informationForHealthcareAnonymiser: SectionAnonymiser<InformationForHealthcareFormData> =
  {
    sectionId: FormSectionSchema.enum.informationForHealthcare,
    fieldAnonymisers: {
      wasHealthcareProvided: preserveAnonymiser,
      hasPersonBeenSearched: preserveAnonymiser,
      hasAdditionalRiskFactors: preserveAnonymiser,
      didSeizeProperty: preserveAnonymiser,
      describeRiskFactors: preserveAnonymiser,
      isUnderInfluence: preserveAnonymiser,
      wasForceUsed: preserveAnonymiser,
      useOfForceType: preserveAnonymiser,
      useOfForceContextHandcuffing: preserveAnonymiser,
      useOfForceContextUnarmedSkills: preserveAnonymiser,
      useOfForceContextDogs: preserveAnonymiser,
      useOfForceContextBaton: preserveAnonymiser,
      useOfForceContextIrritantSpray: preserveAnonymiser,
      useOfForceContextLimbBodyRestraints: preserveAnonymiser,
      useOfForceContextSpitGuard: preserveAnonymiser,
      useOfForceContextShield: preserveAnonymiser,
      useOfForceContextCed: preserveAnonymiser,
      useOfForceContextAep: preserveAnonymiser,
      useOfForceContextFirearms: preserveAnonymiser,
      useOfForceContextOtherImprovised: preserveAnonymiser,
    },
  };

export const section136Anonymiser: FormAnonymiser = {
  formType: IncidentFormTypeSchema.enum.section136,
  sectionAnonymisers: [
    personDetailstMainAnonymiser,
    detentionDetailsAnonymiser,
    informationForHealthcareAnonymiser,
  ],
};

const recordPlaceOfSafetyMainAnonymiser: SectionAnonymiser<RecordPlaceOfSafetyFormData> =
  {
    sectionId: FormSectionSchema.enum.recordPlaceOfSafetyMain,
    fieldAnonymisers: {
      placeOfSafetyType: preserveAnonymiser,
      whyTakingPersonToEmergencyDepartment: preserveAnonymiser,
    },
  };

const recordPlaceOfSafetyArriveAnonymiser: SectionAnonymiser<ArrivePlaceOfSafetyFormData> =
  {
    sectionId: FormSectionSchema.enum.recordPlaceOfSafetyArrival,
    fieldAnonymisers: {
      wasEntryDelayed: preserveAnonymiser,
      howLongWasEntryDelayed: preserveAnonymiser,
      methodOfTransport: preserveAnonymiser,
      whyMethodOfTransportPoliceVehicle: preserveAnonymiser,
      whyWasEntryDelayed: preserveAnonymiser,
    },
  };

const recordPlaceOfSafetyLeaveAnonymiser: SectionAnonymiser<LeavePlaceOfSafetyFormData> =
  {
    sectionId: FormSectionSchema.enum.recordPlaceOfSafetyLeave,
    fieldAnonymisers: {
      leaveReason: preserveAnonymiser,
      leaveAuthorisedBy: preserveAnonymiser,
    },
  };

export const recordPlaceOfSafetyAnonymiser: FormAnonymiser = {
  formType: IncidentFormTypeSchema.enum.recordPlaceOfSafety,
  sectionAnonymisers: [
    recordPlaceOfSafetyMainAnonymiser,
    recordPlaceOfSafetyArriveAnonymiser,
    recordPlaceOfSafetyLeaveAnonymiser,
  ],
};

const requestPolicePresenceMainAnonymiser: SectionAnonymiser<RequestPolicePresenceMainFormData> =
  {
    sectionId: FormSectionSchema.enum.requestPolicePresenceMain,
    fieldAnonymisers: {
      source: preserveAnonymiser,
      mhfUserId: preserveAnonymiser,
    },
  };

export const requestPolicePresenceAnonymiser: FormAnonymiser = {
  formType: IncidentFormTypeSchema.enum.requestPolicePresence,
  sectionAnonymisers: [requestPolicePresenceMainAnonymiser],
};

const finishFormAnonymiser: SectionAnonymiser<FinishFormData> = {
  sectionId: FormSectionSchema.enum.finishForm,
  fieldAnonymisers: {
    whyNotSharing: preserveAnonymiser,
    selectedAssessmentOutcome: preserveAnonymiser,
  },
};

export const finishFormDefaultAnonymiser: FormAnonymiser = {
  formType: IncidentFormTypeSchema.enum.finishForm,
  sectionAnonymisers: [finishFormAnonymiser],
};

const preEndIncidentAnonymiser: SectionAnonymiser<PreEndIncidentFormData> = {
  sectionId: FormSectionSchema.enum.preEndIncident,
  fieldAnonymisers: {
    leavePos: preserveAnonymiser,
    whyNhsNotEngage: preserveAnonymiser,
    whyEndingMcaForm: preserveAnonymiser,
    selectedAssessmentOutcome: preserveAnonymiser,
    arrivalDateTime: preserveAnonymiser,
    dateTimeOfDetention: preserveAnonymiser,
    didNotArrive: preserveAnonymiser,
  },
};

export const preEndIncidentFormAnonymiser: FormAnonymiser = {
  formType: IncidentFormTypeSchema.enum.preEndIncident,
  sectionAnonymisers: [preEndIncidentAnonymiser],
};

const mcaPersonDetailsAnonymiser: SectionAnonymiser<McaPersonDetailsFormData> =
  {
    sectionId: FormSectionSchema.enum.mcaPersonDetails,
    fieldAnonymisers: {
      gender: preserveAnonymiser,
      selfDeclaredEthnicity: preserveAnonymiser,
      relativeOrFriendSameAddress: preserveAnonymiser,
      relativeOrFriendInformed: preserveAnonymiser,
    },
  };

const mcaIncidentDetailsAnonymiser: SectionAnonymiser<McaIncidentDetailsFormData> =
  {
    sectionId: FormSectionSchema.enum.mcaIncidentDetails,
    fieldAnonymisers: {
      bcuIncidentTookPlace: preserveAnonymiser,
      londonBoroughIncidentTookPlace: preserveAnonymiser,
      locationType: preserveAnonymiser,
      confirmCustodySuite: preserveAnonymiser,
      incidentLocation: locationFuzzerAnonymiser,
      custodyNumber: preserveAnonymiser,
    },
  };

const mcaCapacityAssessmentAnonymiser: SectionAnonymiser<McaCapacityAssessmentFormData> =
  {
    sectionId: FormSectionSchema.enum.mcaCapacityAssessment,
    fieldAnonymisers: {
      assessingIndividual: preserveAnonymiser,
      bestInterestPhysicalHealthCondition: preserveAnonymiser,
      useOfForceType: preserveAnonymiser,
      earlyExitCapable: preserveAnonymiser,
      earlyExitNoImpairment: preserveAnonymiser,
      earlyExitNoLackOfCapacity: preserveAnonymiser,
    },
  };

const mentalCapacityActAnonymiser: FormAnonymiser = {
  formType: IncidentFormTypeSchema.enum.mentalCapacityAct,
  sectionAnonymisers: [
    mcaPersonDetailsAnonymiser,
    mcaIncidentDetailsAnonymiser,
    mcaCapacityAssessmentAnonymiser,
  ],
};

const colpSpecificAnonymiser: IncidentWorkItemAnonymiser = {
  coreIncidentAnonymiser: (incidentWorkItem) => incidentWorkItem,

  formAnonymisers: [
    createIncidentAnonymiser,
    triageIncidentAnonymiser,
    section136Anonymiser,
    mentalCapacityActAnonymiser,

    recordPlaceOfSafetyAnonymiser,
    requestPolicePresenceAnonymiser,

    finishFormDefaultAnonymiser,
    preEndIncidentFormAnonymiser,
  ],
};

export const colpAnonymiser = combineIncidentAnonymisers(
  defaultAnonymiser,
  colpSpecificAnonymiser,
);
